import React, { useMemo, useState, useEffect } from 'react';
import matter from 'gray-matter';
import { useThemeContext } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Award } from 'lucide-react';

const QuizVisualizer = ({ content, onMastered }) => {
  const { mode } = useThemeContext();
  const isLight = mode === 'light';

  const { data: metadata } = useMemo(() => {
    try {
      const parsed = matter(content);
      return { data: parsed.data };
    } catch {
      return { data: null };
    }
  }, [content]);

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    setCurrentStep(0);
    setAnswers({});
    setIsCompleted(false);
  }, [content]);

  useEffect(() => {
    if (isCompleted && onMastered) {
      onMastered(true);
    }
  }, [isCompleted, onMastered]);

  if (!metadata || !metadata.questions || metadata.questions.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>
        No quiz data provided for this frame.
      </div>
    );
  }

  const { title, questions, highlights } = metadata;

  // Calculate stats
  const totalQuestions = questions.length;
  let correctCount = 0;
  let wrongCount = 0;
  let skippedCount = 0;

  for (let i = 0; i < currentStep; i++) {
    const q = questions[i];
    const userAns = answers[i];
    if (userAns === undefined) {
      skippedCount++;
    } else if (userAns === q.answer) {
      correctCount++;
    } else {
      wrongCount++;
    }
  }

  if (isCompleted) {
    // Re-calculate for all
    correctCount = 0;
    wrongCount = 0;
    skippedCount = 0;
    questions.forEach((q, i) => {
      const userAns = answers[i];
      if (userAns === undefined) skippedCount++;
      else if (userAns === q.answer) correctCount++;
      else wrongCount++;
    });
  }

  const handleOptionSelect = (idx) => {
    // Prevent changing answer once selected
    if (answers[currentStep] !== undefined) return;
    setAnswers(prev => ({ ...prev, [currentStep]: idx }));
  };

  const handleNext = () => {
    if (currentStep < totalQuestions - 1) {
      setCurrentStep(s => s + 1);
    } else {
      setIsCompleted(true);
    }
  };

  const activeQuestion = questions[currentStep];

  return (
    <div
      style={{
        width: '100%',
        margin: '0 auto',
        background: isLight ? '#ffffff' : '#18181b',
        borderRadius: '24px',
        border: '1px solid var(--border-main)',
        overflow: 'hidden',
        color: 'var(--text-primary)',
        boxShadow: isLight ? '0 12px 40px rgba(0,0,0,0.05)' : '0 12px 40px rgba(0,0,0,0.4)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px 24px',
          background: isLight ? '#f8fafc' : '#121214',
          borderBottom: '1px solid var(--border-main)',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: isLight ? '#e0f2fe' : '#1e3a8a',
            color: isLight ? '#0369a1' : '#bae6fd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Award size={18} />
        </div>
        <div>
          {/* <div style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.5px' }}>Challenge</div> */}
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
        </div>
      </div>

      {!isCompleted ? (
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Progress row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Progress bar */}
            <div style={{ flex: 1, height: '4px', backgroundColor: isLight ? '#e2e8f0' : '#27272a', borderRadius: '2px', overflow: 'hidden', display: 'flex' }}>
              <div style={{ width: `${(currentStep / totalQuestions) * 100}%`, backgroundColor: '#3b82f6', transition: 'width 0.3s ease' }} />
            </div>
            {/* Stats */}
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
              {currentStep + 1} / {totalQuestions}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: isLight ? '#fee2e2' : '#450a0a', color: isLight ? '#b91c1c' : '#fca5a5', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 700 }}>
                <X size={12} strokeWidth={3} /> {wrongCount}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: isLight ? '#dcfce7' : '#052e16', color: isLight ? '#15803d' : '#86efac', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 700 }}>
                <Check size={12} strokeWidth={3} /> {correctCount}
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
            >
              {/* Question Text */}
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>{currentStep + 1}.</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                    {activeQuestion.question}
                  </div>

                  {activeQuestion.code && (
                    <div style={{ marginTop: '16px', padding: '16px', backgroundColor: isLight ? '#f8fafc' : '#09090b', borderRadius: '12px', border: '1px solid var(--border-main)', overflowX: 'auto' }}>
                      <pre style={{ margin: 0, fontSize: '13px', fontFamily: 'JetBrains Mono, monospace', color: isLight ? '#334155' : '#e2e8f0', lineHeight: 1.6 }}>
                        <code>{activeQuestion.code}</code>
                      </pre>
                    </div>
                  )}

                  {/* Options */}
                  <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {activeQuestion.options.map((opt, idx) => {
                      const isSelected = answers[currentStep] === idx;
                      const hasAnswered = answers[currentStep] !== undefined;
                      const isCorrectAnswer = activeQuestion.answer === idx;
                      
                      let borderColor = 'var(--border-main)';
                      let bgColor = 'transparent';
                      let iconRender = null;
                      
                      if (hasAnswered) {
                        if (isCorrectAnswer) {
                          borderColor = isLight ? '#22c55e' : '#16a34a'; // Green
                          bgColor = isLight ? '#f0fdf4' : '#14532d30';
                          iconRender = <Check size={14} strokeWidth={3} color={isLight ? '#16a34a' : '#4ade80'} />;
                        } else if (isSelected) {
                          borderColor = isLight ? '#ef4444' : '#dc2626'; // Red
                          bgColor = isLight ? '#fef2f2' : '#7f1d1d30';
                          iconRender = <X size={14} strokeWidth={3} color={isLight ? '#dc2626' : '#f87171'} />;
                        }
                      } else if (isSelected) {
                        borderColor = '#3b82f6';
                        bgColor = isLight ? '#eff6ff' : '#1e3a8a30';
                      }

                      return (
                        <div
                          key={idx}
                          onClick={() => handleOptionSelect(idx)}
                          style={{
                            padding: '16px',
                            borderRadius: '12px',
                            border: `2px solid ${borderColor}`,
                            backgroundColor: bgColor,
                            cursor: hasAnswered ? 'default' : 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                border: hasAnswered ? 'none' : `2px solid ${isSelected ? '#3b82f6' : (isLight ? '#cbd5e1' : '#3f3f46')}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {!hasAnswered && isSelected && <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#3b82f6' }} />}
                              {hasAnswered && iconRender}
                            </div>
                            <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-primary)' }}>
                              <span style={{ marginRight: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                                {String.fromCharCode(65 + idx)}.
                              </span>
                              {opt}
                            </div>
                          </div>

                          {/* Show explanation inside the selected option box, or inside the correct option box if they answered wrong */}
                          {hasAnswered && (isSelected || isCorrectAnswer) && activeQuestion.explanation && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              style={{ paddingLeft: '32px', marginTop: '4px' }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                {isCorrectAnswer ? (
                                  <span style={{ fontSize: '14px', fontWeight: 700, color: isLight ? '#16a34a' : '#4ade80' }}>
                                    {isSelected ? "That's right!" : "Correct Answer"}
                                  </span>
                                ) : (
                                  <span style={{ fontSize: '14px', fontWeight: 700, color: isLight ? '#dc2626' : '#f87171' }}>
                                    Not quite
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                {isCorrectAnswer || isSelected ? activeQuestion.explanation : ""}
                              </div>
                            </motion.div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Action */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                 <button
                  onClick={handleNext}
                  disabled={answers[currentStep] === undefined}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '20px',
                    backgroundColor: answers[currentStep] === undefined ? (isLight ? '#e2e8f0' : '#27272a') : '#3b82f6',
                    color: answers[currentStep] === undefined ? (isLight ? '#94a3b8' : '#52525b') : '#fff',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '14px',
                    cursor: answers[currentStep] === undefined ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                  }}
                 >
                   {currentStep === totalQuestions - 1 ? 'Finish' : 'Next'} 
                 </button>
              </div>

            </motion.div>
          </AnimatePresence>
        </div>
      ) : (
        /* Results View */
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div>
            <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-primary)' }}>You did it! Quiz complete.</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            <div style={{ padding: '20px', backgroundColor: isLight ? '#f8fafc' : '#18181b', borderRadius: '16px', border: '1px solid var(--border-main)' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Score</div>
              <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--text-primary)' }}>{correctCount}/{totalQuestions}</div>
            </div>
            <div style={{ padding: '20px', backgroundColor: isLight ? '#f8fafc' : '#18181b', borderRadius: '16px', border: '1px solid var(--border-main)' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Accuracy</div>
              <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--text-primary)' }}>{Math.round((correctCount/totalQuestions)*100)}%</div>
            </div>
            <div style={{ padding: '20px', backgroundColor: isLight ? '#f8fafc' : '#18181b', borderRadius: '16px', border: '1px solid var(--border-main)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                <span>Right</span> <span style={{ color: isLight ? '#15803d' : '#4ade80' }}>{correctCount}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                <span>Wrong</span> <span style={{ color: isLight ? '#b91c1c' : '#f87171' }}>{wrongCount}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                <span>Skipped</span> <span>{skippedCount}</span>
              </div>
            </div>
          </div>

          {highlights && highlights.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>Highlights</div>
              <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '12px', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6 }}>
                {highlights.map((hlt, idx) => (
                  <li key={idx} style={{ paddingLeft: '4px' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>{hlt.title}:</strong> {hlt.desc}
                  </li>
                ))}
              </ul>
            </div>
          )}

        </motion.div>
      )}

    </div>
  );
};

export default QuizVisualizer;
